import React, { useEffect, useState } from "react";
import { Mail, MailCheck, MailOpen, AlertTriangle, XCircle, Clock, Loader2 } from "lucide-react";

/**
 * EmailActivityPanel — toont alle e-mails gekoppeld aan een document
 *
 * Gebruik:
 *   <EmailActivityPanel refKind="packing_list" refId={pkl.id} />
 *
 * refKind: "quote" | "invoice" | "packing_list" | "shipping_label" | "order" | "reminder"
 *
 * P1 sprint Feb 2026 — toont per e-mail:
 *   - datum verzonden + ontvanger + onderwerp
 *   - statusbadge (Verzonden / Afgeleverd / Open geregistreerd / Bounce / Mislukt)
 *   - aantal opens + eerste/laatste open
 *   - bounce/foutmelding indien aanwezig
 *
 * Open-tracking is indicatief — wordt duidelijk gecommuniceerd in de UI.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  sent: { label: "Verzonden", className: "bg-blue-50 text-blue-700 border-blue-200", icon: Mail },
  delivered: { label: "Afgeleverd", className: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: MailCheck },
  opened: { label: "Open geregistreerd", className: "bg-violet-50 text-violet-700 border-violet-200", icon: MailOpen },
  bounced: { label: "Bounce", className: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  complained: { label: "Spamklacht", className: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
  failed: { label: "Mislukt", className: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  skipped_no_key: { label: "Geen Resend key", className: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock },
  skipped_no_recipient: { label: "Geen ontvanger", className: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", className: "bg-slate-50 text-slate-600 border-slate-200", icon: Mail };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${meta.className}`}
      data-testid={`email-status-badge-${status}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

export default function EmailActivityPanel({ refKind, refId, title = "E-mail activiteit" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tokenForRefresh = `${refKind || ""}|${refId || ""}`;

  const fetchData = async () => {
    if (!refKind || !refId) return;
    setLoading(true);
    setError(null);
    try {
      const tok = localStorage.getItem("cargo_admin_token");
      const r = await fetch(`${API}/admin/email-activity/by-ref/${refKind}/${refId}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message || "Kon e-mail activiteit niet ophalen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!refKind || !refId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const tok = localStorage.getItem("cargo_admin_token");
        const r = await fetch(`${API}/admin/email-activity/by-ref/${refKind}/${refId}`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (cancelled) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setItems(data.items || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon e-mail activiteit niet ophalen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenForRefresh, refKind, refId]);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 mt-4"
      data-testid="email-activity-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {!loading && items.length > 0 && (
            <span className="text-[11px] text-slate-500">({items.length})</span>
          )}
        </div>
        <button
          onClick={fetchData}
          className="text-[11px] text-slate-500 hover:text-slate-800 underline"
          data-testid="email-activity-refresh"
        >
          Vernieuwen
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-3" data-testid="email-activity-loading">
          <Loader2 className="w-3 h-3 animate-spin" /> Activiteit laden…
        </div>
      )}

      {!loading && error && (
        <div className="text-xs text-red-600 py-3" data-testid="email-activity-error">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="text-xs text-slate-500 py-3 italic"
          data-testid="email-activity-empty"
        >
          Nog geen e-mails verzonden voor dit document.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <ul className="divide-y divide-slate-100" data-testid="email-activity-list">
            {items.map((row) => (
              <li key={row.id} className="py-2.5 text-xs" data-testid={`email-activity-row-${row.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={row.status} />
                      <span className="text-slate-400">{fmtDate(row.created_at)}</span>
                    </div>
                    <div className="mt-1 text-slate-900 font-medium truncate" title={row.subject}>
                      {row.subject || "(geen onderwerp)"}
                    </div>
                    <div className="text-slate-600 truncate" title={row.recipient}>
                      Naar: {row.recipient || "—"}
                    </div>
                  </div>
                  {(row.open_count ?? 0) > 0 && (
                    <div className="text-right text-[11px] text-violet-700 shrink-0">
                      <div className="font-semibold">
                        {row.open_count}× geopend
                      </div>
                    </div>
                  )}
                </div>

                {(row.open_count ?? 0) > 0 && (
                  <div className="ml-1 mt-1 text-[11px] text-slate-600 space-y-0.5">
                    <div>
                      <span className="text-slate-400">Eerste open:</span> {fmtDate(row.first_opened_at)}
                    </div>
                    {row.last_opened_at && row.last_opened_at !== row.first_opened_at && (
                      <div>
                        <span className="text-slate-400">Laatste open:</span> {fmtDate(row.last_opened_at)}
                      </div>
                    )}
                  </div>
                )}

                {row.delivered_at && row.status !== "opened" && (
                  <div className="ml-1 mt-1 text-[11px] text-slate-500">
                    Afgeleverd: {fmtDate(row.delivered_at)}
                  </div>
                )}

                {row.bounce_reason && (
                  <div className="ml-1 mt-1 text-[11px] text-red-600">
                    Bounce: {row.bounce_reason}
                  </div>
                )}

                {row.failure_reason && row.status === "failed" && (
                  <div className="ml-1 mt-1 text-[11px] text-red-600">
                    Foutmelding: {row.failure_reason}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div
            className="mt-2 text-[10px] text-slate-400 italic leading-snug"
            data-testid="email-activity-privacy-note"
          >
            Openregistratie is indicatief. Sommige e-mailprogramma&apos;s (bv. Apple Mail Privacy
            Protection, Gmail caching) blokkeren of automatiseren openmetingen.
          </div>
        </>
      )}
    </div>
  );
}
